import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchCurrencies,
  createCurrency,
  updateCurrency,
  deleteCurrency,
} from "../../features/auth/store/currencySlice";
import { Currency } from "../../features/auth/store/types/currency-types";
import { CurrencyTable } from "./component";
import { ModalContainer } from "../../components/PageComponents";
import { OutlineButton, GradientButton } from "../../components/forms";

const CurrenciesManagementPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const items: Currency[] = useSelector(
    (state: any) => state.currencies?.items ?? [],
  );
  const loading: boolean = useSelector(
    (state: any) => state.currencies?.loading ?? false,
  );
  const saving: boolean = useSelector(
    (state: any) => state.currencies?.saving ?? false,
  );
  const error: string | null = useSelector(
    (state: any) => state.currencies?.error ?? null,
  );

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Currency | null>(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [symbol, setSymbol] = useState("");
  const [exchangeRate, setExchangeRate] = useState<string>("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchCurrencies({ search }));
  }, [dispatch, search]);

  useEffect(() => {
    if (editing) {
      setName(editing.name);
      setCode(editing.code || "");
      setSymbol(editing.symbol);
      setExchangeRate(String(editing.exchange_rate ?? ""));
    } else {
      setName("");
      setCode("");
      setSymbol("");
      setExchangeRate("");
    }
  }, [editing, formOpen]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (item: Currency) => {
    setEditing(item);
    setFormOpen(true);
  };

  const handleDelete = (item: Currency) => {
    if (!window.confirm(`Delete currency ${item.name}?`)) return;
    dispatch(deleteCurrency(item.id));
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const payload = {
      name: name.trim(),
      code: code.trim(),
      symbol: symbol.trim(),
      exchange_rate: parseFloat(exchangeRate),
    };
    if (editing) {
      dispatch(updateCurrency({ id: editing.id, ...payload }));
    } else {
      dispatch(createCurrency(payload));
    }
    setFormOpen(false);
  };

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      ),
    [items],
  );

  const CloseIcon = () => {
    return (
      <button
        onClick={() => setFormOpen(false)}
        className="text-gray-500 hover:text-gray-700 dark:text-zinc-400 dark:hover:text-white transition-colors"
        type="button"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    );
  };

  return (
    <div className="p-4 sm:p-6 dark:bg-zinc-950 min-h-screen transition-colors">
      {/* Header Container */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-semibold dark:text-white">Currencies</h2>
          <p className="text-xs text-gray-500 dark:text-zinc-400 mt-0.5">
            Manage supported currencies, symbols, and exchange rates.
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="w-full sm:w-64 border rounded px-3 py-2 text-sm dark:bg-zinc-900 dark:border-zinc-700 dark:text-white dark:placeholder-zinc-500 outline-none focus:ring-1 focus:ring-blue-500"
          />
          <div className="flex gap-2 w-full sm:w-auto">
            <div className="flex-1 sm:flex-initial">
              <OutlineButton
                onClick={openCreate}
                content="New Currency"
                className="w-full"
              />
            </div>
            <div className="flex-1 sm:flex-initial">
              <GradientButton
                onClick={() => dispatch(fetchCurrencies({ search }))}
                content="Refresh"
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
        <CurrencyTable
          items={sortedItems}
          loading={loading}
          onEdit={openEdit}
          onDelete={handleDelete}
        />
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 mt-2">
            {error}
          </div>
        )}
      </div>

      {/* Form Modal */}
      {formOpen && (
        <ModalContainer
          title={editing ? "Edit Currency" : "Create Currency"}
          closeIcon={<CloseIcon />}
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 space-y-0">
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold mb-1 block dark:text-zinc-300">
                  Name
                </label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full p-2 border rounded dark:bg-zinc-800 dark:border-zinc-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block dark:text-zinc-300">
                  Code
                </label>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="w-full p-2 border rounded dark:bg-zinc-800 dark:border-zinc-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500"
                  maxLength={10}
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block dark:text-zinc-300">
                  Symbol
                </label>
                <input
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value)}
                  className="w-full p-2 border rounded dark:bg-zinc-800 dark:border-zinc-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500"
                  maxLength={10}
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold mb-1 block dark:text-zinc-300">
                  Exchange Rate
                </label>
                <input
                  type="number"
                  step="0.000001"
                  min="0"
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  className="w-full p-2 border rounded dark:bg-zinc-800 dark:border-zinc-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500"
                  required
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <OutlineButton
                onClick={() => setFormOpen(false)}
                content="Cancel"
              />
              <GradientButton
                type="submit"
                disabled={saving}
                content={editing ? "Update" : "Create"}
              />
            </div>
          </form>
        </ModalContainer>
      )}
    </div>
  );
};

export default CurrenciesManagementPage;
